import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import { assertNever } from 'src/replicant/utils';
import LockedButtonOverlay from './LockedButtonOverlay';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { startVisitSequence } from '../visit/visit.sequence';
import StateObserver, { now, user } from '../../StateObserver';
import * as Visit from 'src/replicant/visit';
import { cancelableStatePromise } from '../../lib/statePromise';
import { Feature } from 'src/replicant/analytics';
import { openDuoPopup } from '../duo/duo.sequence';
import animate from '@play-co/timestep-core/lib/animate';
import { isPlatformGuest } from 'src/lib/utils';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { startInviteFriendsSequence } from '../social/social.sequence';
import { openTeamsPopup } from '../minigame/match2-timestep/team/team.sequence';
import TeamChatNotificationBadge from '../minigame/match2-timestep/team/components/TeamChatNotificationBadge';
import FriendshipRequestsBadge from '../friends/FriendshipRequestsBadge';
import { promptLINELoginNew } from '../guest/guest.sequence';
import {
  openFriendsPopup,
  openFriendRequestsPopup,
} from '../friends/friends.sequences';
import { openFriendsListPopup } from '../friends/friendsList.sequences';
import features from 'src/replicant/ruleset/features';
import { openAddFriendPopup } from '../friends/addFriendsV2.sequence';
import { openGiftsListPopup } from '../gifts/gifts.sequence';

const ui = uiConfig.buruburu;

export type FriendsAction =
  | 'teamCafe'
  | 'invite'
  | 'gift'
  | 'visitorList'
  | 'visitFriend'
  | 'duoReceivedList'
  | 'duoSentList'
  | 'friendRequests'
  | 'addFriends';

type FriendsActionButtonOpts = {
  type: FriendsAction;
  onSelect: () => void;
  closeMenu: () => void;
  isLocked?: boolean;
  lockedReason?:
    | 'sleeping'
    | 'dreamDrift'
    | 'visitingFriend'
    | 'dead'
    | 'visitFriend'
    | 'inDaycare';
};

export default class FriendsActionButton extends ButtonScaleViewWithText {
  private isLocked: boolean = false;
  private lockedReason?:
    | 'sleeping'
    | 'dreamDrift'
    | 'visitingFriend'
    | 'dead'
    | 'visitFriend'
    | 'inDaycare';
  private lockedButtonOverlay: LockedButtonOverlay | null = null;

  constructor(private opts: FriendsActionButtonOpts) {
    const { user } = StateObserver.getState();
    const useV2Label = features(user).addFriendsV2;

    let label = i18n(`friendsAction.${opts.type}`);

    // The duo received button should be labeled just "Duo" if the in-game friends feature is enabled.
    if (opts.type === 'duoReceivedList') {
      label = i18n(`friendsAction.duo`);
    }

    // The visitor list button should be labeled just "Visit" if the in-game friends feature is enabled.
    if (opts.type === 'visitorList') {
      label = i18n(`friendsAction.visit`);
    }

    // Override label for addFriends if V2 is enabled and locale is Japanese
    if (opts.type === 'addFriends' && useV2Label) {
      label = i18n('friendsAction.addFriendsV2');
    }

    // Override label for friendRequests when friendList A/B is enabled
    if (opts.type === 'friendRequests') {
      label = features(user).friendList
        ? i18n('friendsAction.friendList')
        : i18n('friendsAction.friends');
    }

    // Determine which icon to use for addFriends based on feature flag
    let iconType: FriendsAction | 'addFriendsV2' = opts.type;
    if (opts.type === 'addFriends') {
      if (features(user).addFriendsV2) {
        iconType = 'addFriendsV2';
      }
    }

    super({
      ...ui.menu.menuButton,
      icon: ui.friends.actionIcons[
        iconType as keyof typeof ui.friends.actionIcons
      ],
      localeText: () => label,
      tracking: { feature: 'friendsMenu', $subFeature: opts.type },
    });

    this.icon?.updateOpts({ scaleMethod: 'contain' });

    if (opts.isLocked) {
      this.setLocked(true, opts.lockedReason);
    }

    if (opts.type === 'teamCafe') {
      this.createTeamsBadge();
    }

    if (opts.type === 'friendRequests') {
      this.createFriendshipBadge();
    }
  }

  public setLocked(
    isLocked: boolean,
    reason?:
      | 'sleeping'
      | 'dreamDrift'
      | 'visitingFriend'
      | 'dead'
      | 'visitFriend'
      | 'inDaycare',
  ) {
    this.lockedReason = reason;
    if (this.isLocked === isLocked) {
      return;
    }

    this.isLocked = isLocked;
    if (isLocked) {
      const { width, height } = this.style;
      this.lockedButtonOverlay = this.addSubview(
        new LockedButtonOverlay({ width, height }),
      );
    } else if (this.lockedButtonOverlay) {
      const overlay = this.lockedButtonOverlay;
      animate(overlay)
        .commit()
        .now({ opacity: 0 }, 250, animate.linear)
        .then(() => overlay.removeFromSuperview());
      this.lockedButtonOverlay = null;
    }
  }

  public async onClick() {
    this.opts.onSelect();

    if (this.isLocked) {
      await openPopupPromise('popupFeatureDisabled', {
        feature: this.opts.type,
        reason: this.lockedReason,
        origin: 'hud',
      });

      return;
    }

    if (isPlatformGuest(platform)) {
      await promptLINELoginNew('friendsMenu');

      return;
    }

    switch (this.opts.type) {
      case 'invite': {
        await startInviteFriendsSequence(
          Feature.INVITE._,
          Feature.INVITE.INVITE_ICON,
        );
        break;
      }
      case 'gift': {
        await openGiftsListPopup('menu');
        break;
      }
      case 'visitorList':
      case 'visitFriend': {
        const result = cancelableStatePromise(({ user }) =>
          Visit.getters.isVisiting(user),
        );
        type Tabs = 'friends' | 'requests' | 'visitors' | undefined;
        const selectedTab: Tabs = 'friends';

        // Wait until visit session starts or user cancels the sequence
        await Promise.race([
          startVisitSequence({ selectedTab }, 'hud'),
          result.promise,
        ]);

        if (Visit.getters.isVisiting(StateObserver.getState().user)) {
          this.opts.closeMenu();
        }

        result.cancel();

        break;
      }

      case 'duoReceivedList':
      case 'duoSentList': {
        const closeMenu = await openDuoPopup({
          tab: this.opts.type === 'duoReceivedList' ? 'received' : 'sent',
          origin: 'hud',
        });

        if (closeMenu) {
          this.opts.closeMenu();
        }

        break;
      }

      case 'teamCafe': {
        await openTeamsPopup({ origin: 'hud' });
        break;
      }

      case 'addFriends': {
        if (features(user()).addFriendsV2) {
          await openAddFriendPopup({ origin: 'hud' });
        } else {
          await openFriendsPopup({
            tab: 'search',
            originFeature: 'menu',
          });
        }
        break;
      }

      case 'friendRequests': {
        if (features(user()).friendList) {
          await openFriendsListPopup({ originFeature: 'menu' });
        } else if (features(user()).addFriendsV2) {
          await openFriendRequestsPopup({ originFeature: 'menu' });
        } else {
          await openFriendsPopup({
            tab: 'requests',
            originFeature: 'menu',
          });
        }
        break;
      }

      default:
        assertNever(this.opts.type);
    }
  }

  private createTeamsBadge() {
    new TeamChatNotificationBadge({
      superview: this,
      size: 45,
      x: this.style.width - 45,
      font: 'Body',
      fontSize: 22,
      textColor: 'white',
      style: 'green',
    });
  }

  private createFriendshipBadge() {
    new FriendshipRequestsBadge({
      superview: this,
      size: 45,
      x: this.style.width - 45,
      font: 'Body',
      fontSize: 22,
      textColor: 'white',
      style: 'red',
    });
  }
}
